import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    Col,
    Row,
} from 'reactstrap';

import {
    Tooltip,
} from '@mui/material';

import { Button, ModalFooter, ModalHeader } from '../design-system/components';
import { DiscardProgressModal } from '../DiscardProgressModal/DiscardProgressModal';
import { Overlay } from '../Overlay/Overlay';

import './NewConnectionModal.scss';

import { ApplicationState } from '../../Store';
import {
    DirectLinkingDisabled,
    FileTemplateUploadEnabled,
    GetActiveConsumerId,
    GetAllUserOptionsDisabled,
    GetDisableAdditionalDocUpload,
    GetDisableAnonymizeResults,
    GetDisableBasisOfAccounting,
    GetDisableProvideuserCopy,
    GetDynamicTenantSelectionSwitch,
    GetFilteredAccountingPackages,
    GetMaxYearsInAdditionToFYTDForCollection,
    GetNumMonthsInImportYTDDropdown,
    GetShareableLinkParamsFlowSwitch,
    NoTimeRestrictionForDataCollection,
    SupportsUniversalConnector,
} from '../../Store/AppSettings';
import {
    ConnectionOptions,
    GetImportCustomerFinancialsActive,
    actionCreators as ImportFinancialsActionCreators,
    ImportMetadata,
    ImportMetadataKeys,
} from '../../Store/ImportFinancials';
import {
    GetCollectionGroupings,
    GetCreationOptions,
    actionCreators as NewDataCollectionParametersActionCreators,
} from '../../Store/NewDataCollectionParameters';
import {
    GetPortalText,
    RetrieveText,
} from '../../Store/PortalAssets';
import { GetBasicSettings, GetTenantName, } from '../../Store/Tenant';
import { GetPortalBusyState, actionCreators as UIStateActions } from '../../Store/UIState';

import { AccountingCollectionGroup, CollectionGroupToName, knownAccountingGroupIds } from '../../Models/AccountingCollectionGroup';
import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';

import { MostRecentDate } from '../../Utils/MostRecentDate';

import {
    AccountingPackage,
    BasisOfAccountingPreference,
    PortalTextCommon,
    PortalTextCommonImport,
    PortalTextImportModals,
    PostLoginUserResponse,
} from '../../Models/Api/strongbox.financialportal';

import { ConnectorType, LinkMode } from '../../Store/ImportFinancials';
import { NewDataCollectionContentGrouping } from '../../Store/NewDataCollectionParameters';
import { GetLoggedInUser } from '../../Store/User';
import { ChooseConnectorTypeModal } from '../ChooseConnectorType/ChooseConnectorTypeModal';
import { ChooseImportModeModal } from '../ChooseImportMode/ChooseImportModeModal';

import { CollectFinancialsImportParameters, OnOffSettings } from './CollectFinancialsImportParameters';
import { SelectImportDataItems } from './SelectImportDataItems';

import { FormatDate, formatStringDTO } from '../../Utils/DateUtils';

import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';
import { AdditionalImportSectionIds } from '../../Utils/Constants';
import { ConfirmModal, ConfirmModalType } from '../ConfirmModal/ConfirmModal';

import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';

import { DataCollectionLimitSummary } from '../DataCollectionLimitSummary/DataCollectionLimitSummary';

type InjectedReduxState = {
    importCustomerFinancialsActive: boolean;
    importOptions: PortalCreateSubmissionRequestOptions;
    shareableLinkParamsActive: boolean;
    maxYearsInAdditionToFYTD: number;
    numberOfMonthsInDropdown?: number;
    disableFileUpload: boolean;
    disableProvideUserCopy: boolean;
    disableDirectLinking: boolean;
    disableBasisOfAccounting: boolean;
    disableAnonymizeResults: boolean;
    allUserOptionsDisabled: boolean;
    collectionContentGroups: NewDataCollectionContentGrouping[];
    collectionGroupDescriptions: AttributedParagraphSet;
    collectionGroupDescriptionsExcel: AttributedParagraphSet;
    noTimeRestrictionForDataCollection: boolean;
    excelImportEnabled: boolean;
    dynamicTenantSelectionEnabled: boolean;
    commonText?: PortalTextCommon;
    importModalText?: PortalTextImportModals;
    commonImportText?: PortalTextCommonImport;
    portalBusy: boolean;
    loggedInUser?: PostLoginUserResponse;
    tenantName: string;
    activeConsumerId: string;
    supportsUniversalConnector: boolean;
    activeAccountingPackages: AccountingPackage[];   // accounting package name ids that are active.
};

type InjectedActionCreators = typeof ImportFinancialsActionCreators & typeof NewDataCollectionParametersActionCreators & typeof UIStateActions;

type ImportCustomerFinancialsContainerProps = {
    directOnly: boolean;
    workspaceName: string;
    workspaceId: string;
    connectionOptions?: ConnectionOptions;
    onSubmit?: (linkMode: LinkMode, connectorType: ConnectorType, options: PortalCreateSubmissionRequestOptions, allowPkgSelection: boolean) => void;
    onCancel?: () => void;
    externallyDisabled?: boolean;
};

type ImportStep = {
    key: string;
    label: string;
    renderIndex: number;
}

type Props = ImportCustomerFinancialsContainerProps & InjectedActionCreators & InjectedReduxState;

const ImportCustomerFinancialsComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        noTimeRestrictionForDataCollection,
        directOnly,
        disableFileUpload,
        disableProvideUserCopy,
        disableAnonymizeResults,
        disableBasisOfAccounting,
        allUserOptionsDisabled,
        importCustomerFinancialsActive,
        onCancel,
        onSubmit,
        importOptions,
        ToggleImportCustomerFinancialsActive,
        disableDirectLinking,
        maxYearsInAdditionToFYTD,
        shareableLinkParamsActive,
        numberOfMonthsInDropdown,
        collectionContentGroups,
        collectionGroupDescriptions,
        collectionGroupDescriptionsExcel,
        InitializeCollectionGroupings,
        dynamicTenantSelectionEnabled,
        importModalText,
        SetPortalWorking,
        SetPortalIdle,
        commonText,
        commonImportText,
        portalBusy,
        loggedInUser,
        excelImportEnabled,
        activeConsumerId,
        supportsUniversalConnector,
        activeAccountingPackages,
        tenantName,
    } = props;

    const submitDataCollectionButtonId = 'submit-data-collection-button';

    const renderChooseConnectorType = (): React.ReactElement => {
        return (
            <ChooseConnectorTypeModal
                connectorType={connectorType}
                onChangeConnectorType={(newType) => {
                    if (excelImportEnabled && newType === ConnectorType.excelUpload) {
                        setLinkMode(LinkMode.direct);
                    }
                    setConnectorType(newType);
                }}
                importModalsText={importModalText}
                activeAccountingPackages={activeAccountingPackages}
                fileConnectorType={supportsUniversalConnector ? ConnectorType.universalConnector : ConnectorType.excelUpload}
            />
        );
    }

    const renderChooseImportType = (): React.ReactElement => {
        return (
            <React.Fragment>
                <ChooseImportModeModal
                    importType={linkMode}
                    onChangeImportType={onLinkModeChange}
                    importModalsText={importModalText}
                    connectorType={connectorType}
                    activeAccountingPackages={activeAccountingPackages}
                    // Neither legacy excel upload nor file upload enabled => the connection-type step is
                    // skipped and this screen is the entry point; surface the supported systems here.
                    showSupportedSystemsBanner={!allowFileUploadStep}
                />
                <Row className={'bottom-row'}>
                    <Col className={'centered-col'}>
                        <DataCollectionLimitSummary />
                    </Col>
                </Row>
            </React.Fragment>
        );
    }

    const [importMetadata, setImportMetadata] = React.useState<ImportMetadata[]>([]);

    // True once the user has pressed Next on the universal-connector options step while the company name was
    // empty. Drives the field's inline "required" error instead of disabling the Next button.
    const [companyNameNextAttempted, setCompanyNameNextAttempted] = React.useState<boolean>(false);

    // Shows the "Discard progress?" confirmation over the wizard when cancelling past the first page.
    const [showDiscardConfirm, setShowDiscardConfirm] = React.useState<boolean>(false);

    const initializeImportMetadata = (): void => {
        setImportMetadata([
            {
                key: ImportMetadataKeys.ucConsumerId,
                value: activeConsumerId,
            },
            {
                key: ImportMetadataKeys.ucMetadataCompanyName,
                value: '',
            },
            {
                key: ImportMetadataKeys.ucMetadataCurrency,
                value: 'USD',
            },
            {
                key: ImportMetadataKeys.ucFiscalEndMonth,
                value: '11',   // months are numberd 0-11
            },
            {
                key: ImportMetadataKeys.ucLoggedInUser,
                value: loggedInUser?.emailAddress || '',
            },
            {
                key: ImportMetadataKeys.ucUniversalConnector,
                value: 'true',
            },
            {
                key: ImportMetadataKeys.ucClientName,
                value: tenantName,
            },
            {
                key: ImportMetadataKeys.ucReportingEndDate,
                value: ''
            },
            {
                key: ImportMetadataKeys.ucBasisOfAccounting,
                value: ''
            },
            {
                key: ImportMetadataKeys.ucNumberOfPeriods,
                value: ''
            }
        ]);
    }

    const updateImportMetadata = (key: string, value: string): void => {
        const iKey = importMetadata.findIndex(m => m.key === key);
        if (iKey < 0) {
            console.error(`updateImportMetadata in ImportCustomerFinancialsContainer saw unrecognized key ${key}`);
            return;
        }
        const newMetadata = importMetadata.slice();
        newMetadata[iKey].value = value;

        setImportMetadata(newMetadata);
    }

    const renderChooseBasicSettings = (): React.ReactElement => {
        // The universal-connector company name is required. Show the inline error once the user has pressed
        // Next with it empty (computed reactively so it clears as soon as they type a value).
        const companyNameValue = importMetadata.find(m => m.key === ImportMetadataKeys.ucMetadataCompanyName)?.value ?? '';
        const companyNameError = companyNameNextAttempted && (companyNameValue.trim().length === 0);

        return (
            <CollectFinancialsImportParameters
                companyNameError={companyNameError}
                showShareableLinkParameters={shareableLinkParamsActive}
                directLink={linkMode === LinkMode.direct}
                disableFileUpload={disableFileUpload}
                disableProvideUserCopy={disableProvideUserCopy}
                disableBasisOfAccounting={disableBasisOfAccounting}
                disableAnonymizedResults={disableAnonymizeResults}
                allUserOptionsDisabled={allUserOptionsDisabled}
                parameters={importOptionsInUse}
                maxCollectionPeriods={maxYearsInAdditionToFYTD}
                onMostRecentMonthChange={onMostRecentMonthChange}
                onPeriodsToCollectChanged={onPeriodsToCollectChanged}
                onToggleOnOffSetting={onToggleOnOffSetting}
                onBasisOfAccountingChanged={onBasisOfAccountingChanged}
                numberOfMonthsInDropdown={numberOfMonthsInDropdown}
                noTimeRestrictionForDataCollection={noTimeRestrictionForDataCollection}
                linkMode={linkMode}
                commonText={commonText}
                importModalsText={importModalText}
                connectorType={connectorType}
                importMetadata={importMetadata}
                updateMetadata={updateImportMetadata}
            />
        )
    }

    const [collectionContentGroupsInUse, setCollectionContentGroupsInUse] = React.useState<NewDataCollectionContentGrouping[]>([]);

    const renderSelectAccountingDataToCollect = (): React.ReactElement => {
        return (
            <SelectImportDataItems
                collectionContentGroups={collectionContentGroupsInUse}
                onContentEnablementChange={onCollectionGroupEnablementChange}
                importModalsText={importModalText}
            />
        );
    }

    const [connectorType, setConnectorType] = React.useState<ConnectorType>(ConnectorType.fullIntegration);

    const [linkMode, setLinkMode] = React.useState<LinkMode>(LinkMode.shareable);

    // mostRecentPeriodMonth will be set immediately by a callback onMostRecentMonthChange.
    // If the year is lte 0 and month is lt 0, the modal will treat this as undefined and
    // that will trigger a callback. The parameters to the callback will be the
    // first available date shown in the dropdown from which the user selects a month.

    const [importOptionsInUse, setImportOptionsInUse] = React.useState<PortalCreateSubmissionRequestOptions>(importOptions);

    const [currentStep, setCurrentStep] = React.useState<number>(0);

    const [errorNoGroupsSelected, setErrorNoGroupsSelected] = React.useState<boolean>(false);
    const [pageButtonEnablement, setPageButtonEnablement] = React.useState<{ nextButton: boolean, backButton: boolean }>({
        nextButton: true,
        backButton: false,
    });

    // This is a little weird.  Because of the way React handles dependencies,
    // if we keep this in importSteps, unless we enumerate every depedency that
    // gets passed to each of the components in these methods, we end up with
    // invocations that have stale parameters. It's more efficient to redirect
    // through this array than specifying the functions as a property in the
    // ImportStep object.

    const stepRenderers: (() => React.ReactElement)[] = [
        renderChooseImportType,
        renderChooseBasicSettings,
        renderSelectAccountingDataToCollect,
        renderChooseConnectorType,
    ];

    React.useEffect(() => {
        const today = new Date();

        setImportOptionsInUse({
            ...importOptionsInUse,
            mostRecentPeriodMonth: today.getMonth(),
            mostRecentPeriodYear: today.getFullYear(),
            mostRecentPeriodDay: today.getDate(),
        });

        initializeImportMetadata();

        // I want this to run like componentDidMount so this is the correct dependency list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial value for stepParameters is set up for native connectors, so it collects accounting groups
    // and does not include the connect with universal collector immediate step.

    const [stepParameters, setStepParameters] = React.useState<{
        totalSteps: number;
        importStepChooseConnectorType: number;
        importStepChooseMode: number;
        importStepChooseBasicOptions: number;
        importStepChooseAccountingGroups: number;
    }>(
        {
            totalSteps: 3,
            importStepChooseConnectorType: -1,
            importStepChooseMode: 1,
            importStepChooseBasicOptions: 2,
            importStepChooseAccountingGroups: 3,
        }
    )

    // In the steps below, renderIndex refers to the index in stepRenderers that is used
    // for rendering the step.  So for the first entry in fullImportSteps below, renderIndex: 3
    // for chooseConnectorType refers to stepRenderers[3] or renderChooseConnectorType.
    //
    // The steps themselves are just the ordered steps that apply to the modal.

    const flowImportSteps = React.useMemo(() => {
        const nativeImportSteps = [
            {
                key: 'chooseConnectorType',
                label: importModalText?.importStepsConnectorType || '',
                renderIndex: 3,
            },
            {
                key: 'selectMode',
                label: importModalText?.importStepsType || '',
                renderIndex: 0,
            },
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            },
            {
                key: 'selectAccountingData',
                label: importModalText?.importStepsData || '',
                renderIndex: 2,
            }
        ];

        const excelImportSteps = [
            {
                key: 'chooseConnectorType',
                label: importModalText?.importStepsConnectorType || '',
                renderIndex: 3,
            },
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            },
            {
                key: 'selectAccountingData',
                label: importModalText?.importStepsData || '',
                renderIndex: 2,
            }
        ];

        const nativeImportStepsUniversalDisabled = [
            {
                key: 'selectMode',
                label: importModalText?.importStepsType || '',
                renderIndex: 0,
            },
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            },
            {
                key: 'selectAccountingData',
                label: importModalText?.importStepsData || '',
                renderIndex: 2,
            }
        ];

        const universalImportStepsDirect = [
            {
                key: 'chooseConnectorType',
                label: importModalText?.importStepsConnectorType || '',
                renderIndex: 3,
            },
            {
                key: 'selectMode',
                label: importModalText?.importStepsType || '',
                renderIndex: 0,
            },
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            }
        ]

        const universalImportStepsSecureURL = [
            {
                key: 'chooseConnectorType',
                label: importModalText?.importStepsConnectorType || '',
                renderIndex: 3,
            },
            {
                key: 'selectMode',
                label: importModalText?.importStepsType || '',
                renderIndex: 0,
            },
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            }
        ]

        const noModeImportSteps = [
            {
                key: 'chooseBasicOptions',
                label: importModalText?.importStepsOptions || '',
                renderIndex: 1,
            },
            {
                key: 'selectAccountingData',
                label: importModalText?.importStepsData || '',
                renderIndex: 2,
            }
        ];

        return {
            nativeImportSteps,
            nativeImportStepsUniversalDisabled,
            universalImportStepsDirect,
            universalImportStepsSecureURL,
            noModeImportSteps,
            excelImportSteps,
        }
    }, [importModalText])

    const allowFileUploadStep = React.useMemo(() => {
        return supportsUniversalConnector || excelImportEnabled;
    }, [supportsUniversalConnector, excelImportEnabled])

    const [importSteps, setImportSteps] = React.useState<ImportStep[]>
        (allowFileUploadStep ? flowImportSteps.nativeImportSteps : flowImportSteps.nativeImportStepsUniversalDisabled);

    React.useEffect(() => {
        // if there's no value in the first label of import steps then when it was initialized there was no text 
        // available.  If we are in a state now where flowImportSTeps.nativeImportSteps has a value the reinitialize
        // import steps with the current values in flowImportSteps so the labels have values.

        if ((flowImportSteps.nativeImportSteps[0].label.length > 0) && (importSteps[0].label.length <= 0)) {
            if (directOnly) {
                setImportSteps(flowImportSteps.noModeImportSteps);
            } else {
                const base = allowFileUploadStep ? flowImportSteps.nativeImportSteps : flowImportSteps.nativeImportStepsUniversalDisabled;
                // When direct linking is disabled the mode page is skipped, so keep the step count consistent
                // with stepParameters by dropping the 'selectMode' step here too.
                setImportSteps(disableDirectLinking ? base.filter(s => s.key !== 'selectMode') : base);
            }
        }

        // We setImportSteps above so specifically do not want it in the dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowImportSteps])

    // skipModePage removes the "secure URL vs direct connection" step from the flow. It is used when direct
    // linking is disabled: the connector-type page still shows, but the mode choice is skipped and a secure URL
    // is always created. The stepParameters indices are derived from the (possibly filtered) step array so they
    // stay correct regardless of which steps are present.
    const setStepsForSecureURLEnabled = (connectorType: ConnectorType, linkMode: LinkMode, skipModePage: boolean = false): void => {
        let steps: ImportStep[];
        if (connectorType === ConnectorType.fullIntegration) {
            // Can only get here when allowFileUploadStep is set if it's universal collector
            steps = allowFileUploadStep ? flowImportSteps.nativeImportSteps : flowImportSteps.nativeImportStepsUniversalDisabled;
        } else if (connectorType === ConnectorType.excelUpload) {
            steps = flowImportSteps.excelImportSteps;
        } else {
            if (linkMode === LinkMode.direct) {
                steps = flowImportSteps.universalImportStepsDirect;
            } else {
                steps = flowImportSteps.universalImportStepsSecureURL;
            }
        }

        const finalSteps = skipModePage ? steps.filter(s => s.key !== 'selectMode') : steps;

        setImportSteps(finalSteps.slice());
        setStepParameters({
            totalSteps: finalSteps.length,
            importStepChooseConnectorType: finalSteps.findIndex(s => s.key === 'chooseConnectorType'),
            importStepChooseMode: finalSteps.findIndex(s => s.key === 'selectMode'),
            importStepChooseBasicOptions: finalSteps.findIndex(s => s.key === 'chooseBasicOptions'),
            importStepChooseAccountingGroups: finalSteps.findIndex(s => s.key === 'selectAccountingData'),
        });
    }

    React.useEffect(() => {
        if (directOnly) {
            // "Import additional financials" (relink): no connector-type or mode pages, always a direct connect.
            setImportSteps(flowImportSteps.noModeImportSteps.slice());
            setStepParameters({
                totalSteps: flowImportSteps.noModeImportSteps.length,
                importStepChooseConnectorType: -1,
                importStepChooseMode: -1,
                importStepChooseBasicOptions: 0,
                importStepChooseAccountingGroups: 1,
            });
            setCurrentStep(0);
            setLinkMode(LinkMode.direct);
            setConnectorType(ConnectorType.fullIntegration);
        } else if (disableDirectLinking) {
            // Direct linking disabled: keep the connector-type page, but always create a secure URL and skip the
            // "secure URL vs direct connection" page. The rest of the pages (options, data) behave as normal.
            setLinkMode(LinkMode.shareable);
            setStepsForSecureURLEnabled(connectorType, LinkMode.shareable, true);
            setCurrentStep(0);
        } else {
            setStepsForSecureURLEnabled(connectorType, linkMode);
            setCurrentStep(0);   // Okay to set current step to 0 here.  The only place where connector type would change is the first page
        }

        // These are the only dependencies I really want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableDirectLinking, directOnly, connectorType])

    React.useEffect(() => {
        // Next is always enabled; the universal-connector company-name requirement is enforced on click
        // (handleOnSubmit) with an inline field error rather than by disabling the button.
        setPageButtonEnablement({
            nextButton: true,
            backButton: currentStep > 0,
        });
    }, [currentStep])

    React.useEffect(() => {
        if (directOnly) {
            // relink flow keeps its fixed steps; nothing to recompute here.
        } else if (disableDirectLinking) {
            // Keep the connector-type page in sync when the user switches connector, still forcing a secure URL
            // and skipping the mode page.
            setStepsForSecureURLEnabled(connectorType, LinkMode.shareable, true);
        } else {
            setStepsForSecureURLEnabled(connectorType, linkMode);
        }
        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkMode, allowFileUploadStep, connectorType])

    React.useEffect(() => {
        if (importCustomerFinancialsActive) {
            const workingId = 'ImportFinancials:GetText';

            SetPortalWorking(workingId);
            RetrieveText(AdditionalImportSectionIds)
                .finally(() => {
                    SetPortalIdle(workingId);
                });

            setLinkMode(directOnly ? LinkMode.direct : LinkMode.shareable);
            setConnectorType(ConnectorType.fullIntegration);
            setCurrentStep(0);
            setCompanyNameNextAttempted(false);
            initializeImportMetadata();
        }
        // importCustomerFinancialsActive is the only dependency I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importCustomerFinancialsActive])

    React.useEffect(() => {
        if (connectorType === ConnectorType.excelUpload) {
            InitializeCollectionGroupings(collectionGroupDescriptionsExcel, dynamicTenantSelectionEnabled);
            setCollectionContentGroupsInUse([]);
        } else {
            InitializeCollectionGroupings(collectionGroupDescriptions, dynamicTenantSelectionEnabled);
            setCollectionContentGroupsInUse([]);
        }

        // These are the only dependencies I really want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectorType, linkMode, importModalText]);

    React.useEffect(() => {
        let groupsToSet = collectionContentGroups.map((grp) => {
            return {
                ...grp,
                collectionGroup: {
                    ...grp.collectionGroup,
                    name: CollectionGroupToName(grp.collectionGroup.id, commonImportText),
                }
            }
        });

        if (connectorType === ConnectorType.excelUpload) {
            groupsToSet = collectionContentGroups.map((grp) => {
                if (grp.collectionGroup.id === knownAccountingGroupIds.financialStatements) {
                    return {
                        ...grp,
                        selected: true,
                        collectionGroup: {
                            ...grp.collectionGroup,
                            readOnly: true,
                            collect: true,
                            name: CollectionGroupToName(grp.collectionGroup.id, commonImportText),
                        }
                    };
                } else {
                    return {
                        ...grp,
                        selected: false,
                        collectionGroup: {
                            ...grp.collectionGroup,
                            readOnly: true,
                            collect: false,
                            name: CollectionGroupToName(grp.collectionGroup.id, commonImportText),
                        }
                    };
                }
            });
        }

        groupsToSet = groupsToSet.sort((g1, g2) => {
            if (g1.collectionGroup.id === knownAccountingGroupIds.financialStatements) {
                return -1;
            } else if (g2.collectionGroup.id === knownAccountingGroupIds.financialStatements) {
                return 1;
            }
            if (g1.collectionGroup.id === knownAccountingGroupIds.transactions) {
                return -1;
            } else if (g2.collectionGroup.id === knownAccountingGroupIds.transactions) {
                return 1;
            }
            if (g1.collectionGroup.id === knownAccountingGroupIds.accountsPayable) {
                return -1;
            } else if (g2.collectionGroup.id === knownAccountingGroupIds.accountsPayable) {
                return 1;
            }
            return 1;
        })

        setCollectionContentGroupsInUse(groupsToSet);

        // collectionContentGroups and linkMode are the only variables that need to be in the dependency list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectorType, collectionContentGroups, linkMode, commonImportText]);

    const onLinkModeChange = (linkMode: LinkMode): void => {
        setLinkMode(linkMode);
        if (!!importOptionsInUse) {
            setImportOptionsInUse({
                ...importOptionsInUse,
                allowUserUpload: false,
                provideUserCopy: false,
            })
        }
    }

    const toggleModal = (): void => {
        ToggleImportCustomerFinancialsActive();
        onCancel && onCancel();
    }

    // Cancelling (footer Cancel or header X) on any page after the first prompts a "Discard progress?"
    // confirmation. The first page (whichever it is for this tenant's flow) closes immediately.
    const requestClose = (): void => {
        if (currentStep > 0) {
            setShowDiscardConfirm(true);
        } else {
            toggleModal();
        }
    }

    const onMostRecentMonthChange = (newValue: MostRecentDate): void => {
        // If we pass undefined for parameters down to ImportCustomerFinancialsModal, this
        // will never be called so this is really just a sanity check.
        if (!!importOptionsInUse) {
            setImportOptionsInUse({
                ...importOptionsInUse,
                mostRecentPeriodMonth: newValue.month,
                mostRecentPeriodYear: newValue.year,
                mostRecentPeriodDay: newValue.day,
            });
        }
    }

    const onPeriodsToCollectChanged = (newValue: number): void => {
        // If we pass undefined for parameters down to ImportCustomerFinancialsModal, this
        // will never be called so this is really just a sanity check.
        if (!!importOptionsInUse) {
            setImportOptionsInUse({
                ...importOptionsInUse,
                fullFiscalYearsToCollect: newValue
            });
        }
    }

    const changeCollectionKey = (collectionGroups: NewDataCollectionContentGrouping[], key: string, enabled: boolean): void => {
        const groupIndex = collectionGroups.findIndex(g => g.collectionGroup.id === key);
        if (groupIndex === -1) {
            return;
        }

        const group = collectionGroups[groupIndex];
        collectionGroups[groupIndex] = {
            ...group,
            selected: enabled,
        }
    }

    const onCollectionGroupEnablementChange = (key: string, enabled: boolean): void => {
        const newCollectionGroups = collectionContentGroupsInUse.slice();

        switch (key) {
            case knownAccountingGroupIds.financialStatements:
                changeCollectionKey(newCollectionGroups, knownAccountingGroupIds.financialStatements, enabled);
                break;
            case knownAccountingGroupIds.transactions:
                changeCollectionKey(newCollectionGroups, knownAccountingGroupIds.transactions, enabled);
                break;
            case knownAccountingGroupIds.accountsReceivable:
                changeCollectionKey(newCollectionGroups, knownAccountingGroupIds.accountsReceivable, enabled);
                break;
            case knownAccountingGroupIds.accountsPayable:
                changeCollectionKey(newCollectionGroups, knownAccountingGroupIds.accountsPayable, enabled);
                break;
        }

        setCollectionContentGroupsInUse(newCollectionGroups);
    }

    const onBasisOfAccountingChanged = (newValue: BasisOfAccountingPreference): void => {
        if (!!importOptionsInUse) {
            setImportOptionsInUse({
                ...importOptionsInUse,
                basisOfAccountingPreference: newValue
            });
        }
    }

    const onToggleOnOffSetting = (setting: OnOffSettings): void => {
        // If we pass undefined for parameters down to ImportCustomerFinancialsModal, this
        // will never be called so this is really just a sanity check.
        switch (setting) {
            case OnOffSettings.allowUserUpload:
                if (!!importOptionsInUse) {
                    setImportOptionsInUse({
                        ...importOptionsInUse,
                        allowUserUpload: !importOptionsInUse.allowUserUpload,
                    });
                }
                break;
            case OnOffSettings.anonymizeCustomersAndVendors:
                if (!!importOptionsInUse) {
                    setImportOptionsInUse({
                        ...importOptionsInUse,
                        anonymizeCustomersAndVendors: !importOptionsInUse.anonymizeCustomersAndVendors,
                    });
                }
                break;
            case OnOffSettings.userGetsCopy:
                if (!!importOptionsInUse) {
                    setImportOptionsInUse({
                        ...importOptionsInUse,
                        provideUserCopy: !importOptionsInUse.provideUserCopy,
                    });
                }
                break;
            default:
                break;
        }
    }

    const setMetadataValue = (key: string, value: string): void => {
        const iKey = importMetadata.findIndex((m) => m.key === key);
        if (iKey < 0) {
            // This really shouldn't be possible.
            console.error(`Expected metadata value not found ${key}`);
            return;
        }
        importMetadata[iKey].value = value;
    }

    const startImport = (allowPkgSelection: boolean): void => {
        const atLeast1GroupSelected = collectionContentGroupsInUse.find(g => g.selected);

        if (!atLeast1GroupSelected) {
            setErrorNoGroupsSelected(true);
            return;
        }

        let submitParameters = {
            ...importOptionsInUse
        }

        if (linkMode !== LinkMode.shareable) {
            // Don't really need to do this but it's more complete
            if (!!importOptionsInUse) {
                setImportOptionsInUse({
                    ...importOptionsInUse,
                    allowUserUpload: false,
                    provideUserCopy: false,
                });
            }
        }

        submitParameters.accountingCollectionGroups = new Map<string, AccountingCollectionGroup>();

        collectionContentGroupsInUse.forEach(group => {
            submitParameters.accountingCollectionGroups.set(group.collectionGroup.id, {
                id: group.collectionGroup.id,
                collect: group.selected
            });
        });

        submitParameters.hasShareableLink = (linkMode === LinkMode.shareable);

        if (connectorType === ConnectorType.universalConnector) {
            submitParameters.metadata = [];

            setMetadataValue(ImportMetadataKeys.ucBasisOfAccounting, importOptionsInUse.basisOfAccountingPreference.toString());
            setMetadataValue(ImportMetadataKeys.ucNumberOfPeriods, (importOptionsInUse.fullFiscalYearsToCollect + 1).toString());
            setMetadataValue(ImportMetadataKeys.ucReportingEndDate,
                FormatDate(
                    new Date(importOptionsInUse.mostRecentPeriodYear, importOptionsInUse.mostRecentPeriodMonth, importOptionsInUse.mostRecentPeriodDay),
                    formatStringDTO
                )
            );

            const indexCompanyName = importMetadata.findIndex(m => m.key === ImportMetadataKeys.ucMetadataCompanyName);
            if ((indexCompanyName < 0) || (!importMetadata[indexCompanyName].value) || (importMetadata[indexCompanyName].value.length <= 0)) {
                // Shouldn't be possible with empty field checking and such in the modal
                console.error('Setting universal connector import parameters, company name is not provided');
                return;
            }

            importMetadata.forEach(m => {
                // If it's a shareable link we shouldn't pass the logged in user name so get rid of it.
                if ((m.key !== ImportMetadataKeys.ucLoggedInUser) || (linkMode !== LinkMode.shareable)) {
                    submitParameters.metadata!.push({
                        id: m.key.toString(),
                        value: m.value,
                        name: m.key.toString(),
                        displayFlags: 0,
                        type: 'None'
                    });
                }
            })
        }

        onSubmit && onSubmit(linkMode, connectorType, submitParameters, allowPkgSelection);

        toggleModal();
    }

    const handleOnSubmit = (allowPkgSelection: boolean): void => {
        // Universal connector: company name is required. Rather than disabling Next, block advancement here
        // and flag the field so it shows a red border + "required" message.
        if ((connectorType === ConnectorType.universalConnector) && (currentStep === stepParameters.importStepChooseBasicOptions)) {
            const cnIndex = importMetadata.findIndex(m => m.key === ImportMetadataKeys.ucMetadataCompanyName);
            const companyName = cnIndex >= 0 ? importMetadata[cnIndex].value : '';
            if (!companyName || companyName.trim().length === 0) {
                setCompanyNameNextAttempted(true);
                return;
            }
        }

        if (currentStep >= stepParameters.totalSteps - 1) {
            startImport(allowPkgSelection);
        } else {
            setCurrentStep(currentStep + 1);
        }
    }

    const handlePrevious = (): void => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }

    if (!importCustomerFinancialsActive) {
        return (<></>);
    }

    return (
        <>
            <Overlay>
                <div
                    className={'cr-modal page-modal page-modal--min-height'}
                    style={{ width: 800 }}
                >
                    <ModalHeader title={importSteps[currentStep]?.label || ''} onClose={requestClose} />
                    <div className={'cr-modal-body page-modal-body'}>
                        {portalBusy && (
                            <LoadingIndicator active={true} />
                        )}

                        {!portalBusy && errorNoGroupsSelected && (
                            <ConfirmModal
                                msg={importModalText?.errorNoAccountingDataSelected || ''}
                                title={commonText?.error || ''}
                                modalType={ConfirmModalType.ok}
                                onTerminalButton={() => {
                                    setErrorNoGroupsSelected(false);
                                    setCurrentStep(stepParameters.totalSteps - 1);
                                }}
                                commonText={commonText}
                            />
                        )}

                        {!portalBusy && (currentStep < stepParameters.totalSteps) && (currentStep >= 0) &&
                            stepRenderers[importSteps[currentStep].renderIndex]()}

                        {/* Or show an error, this should be purely a stopgap picked up in QA */}
                        {!portalBusy && ((currentStep >= stepParameters.totalSteps) || (currentStep < 0)) && (
                            <p className={'page-body-text'}>
                                {importModalText?.internalErrorStepOutOfRange || ''}
                            </p>
                        )}
                    </div>
                    <ModalFooter
                        tertiaryAction={
                            <Button type={'floating'} onClick={requestClose}>{commonText?.cancel || ''}</Button>
                        }
                    >
                        {pageButtonEnablement.backButton && (
                            <Tooltip title={importModalText?.previousOptionsPage || ''}>
                                <span>
                                    <Button
                                        type={'secondary'}
                                        className={'page-btn--min-width'}
                                        onClick={() => handlePrevious()}
                                        disabled={portalBusy || !pageButtonEnablement.backButton}
                                    >
                                        {commonText?.backButton || ''}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title={importModalText?.nextOptionsPage || ''}>
                            <span>
                                <Button
                                    id={submitDataCollectionButtonId}
                                    type={'primary'}
                                    className={'page-btn--min-width'}
                                    onClick={() => handleOnSubmit(!directOnly)}
                                    disabled={portalBusy || !pageButtonEnablement.nextButton}
                                >
                                    {commonText?.nextButton || ''}
                                </Button>
                            </span>
                        </Tooltip>
                    </ModalFooter>
                </div>
            </Overlay>
            {showDiscardConfirm && (
                <DiscardProgressModal
                    onKeepEditing={() => setShowDiscardConfirm(false)}
                    onDiscard={() => {
                        setShowDiscardConfirm(false);
                        toggleModal();
                    }}
                />
            )}
        </>
    )
}

export const ImportCustomerFinancials = connect<InjectedReduxState, InjectedActionCreators, ImportCustomerFinancialsContainerProps, ApplicationState>(
    (appState: ApplicationState) => {
        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const importModalText = GetPortalText<PortalTextImportModals>(appState, 'importModals');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        const result = {
            importCustomerFinancialsActive: GetImportCustomerFinancialsActive(appState),
            shareableLinkParamsActive: GetShareableLinkParamsFlowSwitch(appState),
            importOptions: GetCreationOptions(appState, commonImportText),
            maxYearsInAdditionToFYTD: GetMaxYearsInAdditionToFYTDForCollection(appState),
            numberOfMonthsInDropdown: GetNumMonthsInImportYTDDropdown(appState),
            disableFileUpload: GetDisableAdditionalDocUpload(appState),
            disableProvideUserCopy: GetDisableProvideuserCopy(appState),
            disableBasisOfAccounting: GetDisableBasisOfAccounting(appState),
            disableAnonymizeResults: GetDisableAnonymizeResults(appState),
            allUserOptionsDisabled: GetAllUserOptionsDisabled(appState),
            disableDirectLinking: DirectLinkingDisabled(appState),
            collectionContentGroups: GetCollectionGroupings(appState),
            loggedInUser: GetLoggedInUser(appState),
            tenantBasicSettings: GetBasicSettings(appState),
            activeConsumerId: GetActiveConsumerId(appState),
            collectionGroupDescriptions: {
                paragraphs: [
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.financialStatements }
                        ],
                        content: importModalText?.fsGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.transactions }
                        ],
                        content: importModalText?.txnsGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.accountsPayable }
                        ],
                        content: importModalText?.apGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.accountsReceivable }
                        ],
                        content: importModalText?.arGroupDescription || '',
                    }
                ]
            },
            collectionGroupDescriptionsExcel: {
                paragraphs: [
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.financialStatements }
                        ],
                        content: importModalText?.excelFSGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.transactions }
                        ],
                        content: importModalText?.excelTxnsGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.accountsReceivable }
                        ],
                        content: importModalText?.excelARGroupDescription || '',
                    },
                    {
                        attributes: [
                            { name: 'id', value: knownAccountingGroupIds.accountsPayable }
                        ],
                        content: importModalText?.excelAPGroupDescription || '',
                    },
                ]
            },
            noTimeRestrictionForDataCollection: NoTimeRestrictionForDataCollection(appState),
            excelImportEnabled: FileTemplateUploadEnabled(appState),
            dynamicTenantSelectionEnabled: GetDynamicTenantSelectionSwitch(appState),
            commonText,
            importModalText,
            portalBusy: GetPortalBusyState(appState),
            commonImportText,
            supportsUniversalConnector: SupportsUniversalConnector(appState),
            activeAccountingPackages: GetFilteredAccountingPackages(appState),
            tenantName: GetTenantName(appState),
        };

        // UC overrides excel so if UC is on, excel import should always be off.
        if (result.supportsUniversalConnector) {
            result.excelImportEnabled = false;
        }

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...ImportFinancialsActionCreators,
            ...NewDataCollectionParametersActionCreators,
            ...UIStateActions,
        },
        dispatch
    )
)(ImportCustomerFinancialsComponent);

